import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Star, Shield, Clock, Award } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services, testimonials } from '../mockData';

export const Home = () => {
  const { t } = useTranslation();
  
  const benefits = [
    { icon: Shield, title: t('home.benefits.durability.title'), description: t('home.benefits.durability.description') },
    { icon: Clock, title: t('home.benefits.installation.title'), description: t('home.benefits.installation.description') },
    { icon: Award, title: t('home.benefits.quality.title'), description: t('home.benefits.quality.description') },
    { icon: Star, title: t('home.benefits.satisfaction.title'), description: t('home.benefits.satisfaction.description') }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1772300704502-410f0fbd43bb"
            alt="Piso epóxico profesional"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/85 to-black/70"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
            {t('home.hero.title')}
            <span className="block mt-2 bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
              {t('home.hero.titleHighlight')}
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-slate-200 mb-8 max-w-3xl mx-auto">
            {t('home.hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/roi">
              <Button size="lg" className="bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white text-lg px-8 py-6 shadow-lg shadow-orange-600/50">
                {t('home.hero.viewROI')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contacto">
              <Button size="lg" variant="outline" className="border-2 border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-black text-lg px-8 py-6">
                {t('home.hero.contactNow')}
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-orange-500/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-orange-500/50 rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-orange-900/30 bg-black shadow-xl shadow-orange-900/10 hover:shadow-2xl hover:shadow-orange-600/20 transition-all">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-black" />
                  </div>
                  <h3 className="text-xl font-bold text-orange-500 mb-2">{benefit.title}</h3>
                  <p className="text-orange-600/70">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-orange-500 mb-4">
              {t('home.servicesTitle')}
            </h2>
            <p className="text-xl text-orange-600/70 max-w-2xl mx-auto">
              {t('home.servicesSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.slice(0, 4).map((service) => {
              // Map service ID to translation key
              const serviceKey = service.id === 1 ? 'industrial' 
                : service.id === 2 ? 'commercial' 
                : service.id === 3 ? 'residential' 
                : service.id === 4 ? 'parking'
                : 'countertops';
              
              return (
                <Card key={service.id} className="group overflow-hidden border-orange-900/30 bg-zinc-900 shadow-lg hover:shadow-2xl hover:shadow-orange-600/20 transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-white font-bold text-lg">
                        {service.title}
                      </h3>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <p className="text-orange-600/70 text-sm mb-4">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/servicios">
              <Button size="lg" className="bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white shadow-lg">
                {t('home.viewAllServices')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-orange-500 mb-4">
              {t('home.testimonialsTitle')}
            </h2>
            <p className="text-xl text-orange-600/70">
              {t('home.testimonialsSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-orange-900/30 bg-black shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`star-${testimonial.id}-${i}`} className="w-5 h-5 fill-orange-500 text-orange-500" />
                    ))}
                  </div>
                  <p className="text-orange-600/70 mb-6 italic">
                    "{t(`mockData.testimonials.${testimonial.id === 1 ? 'carlos' : testimonial.id === 2 ? 'ana' : 'roberto'}.text`)}"
                  </p>
                  <div className="flex items-center">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full mr-4 border-2 border-orange-600/30"
                    />
                    <div>
                      <p className="font-bold text-orange-500">
                        {t(`mockData.testimonials.${testimonial.id === 1 ? 'carlos' : testimonial.id === 2 ? 'ana' : 'roberto'}.name`)}
                      </p>
                      <p className="text-sm text-orange-600/70">
                        {t(`mockData.testimonials.${testimonial.id === 1 ? 'carlos' : testimonial.id === 2 ? 'ana' : 'roberto'}.role`)}
                      </p>
                      <p className="text-sm text-orange-600/50">
                        {t(`mockData.testimonials.${testimonial.id === 1 ? 'carlos' : testimonial.id === 2 ? 'ana' : 'roberto'}.company`)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-zinc-900 to-black border-t border-orange-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-orange-500 mb-6">
            {t('home.cta.title')}
          </h2>
          <p className="text-xl text-orange-600/70 mb-8">
            {t('home.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/roi">
              <Button size="lg" className="bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white text-lg px-8 py-6 shadow-lg shadow-orange-600/50">
                {t('home.cta.calculateSavings')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contacto">
              <Button size="lg" variant="outline" className="border-2 border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-black text-lg px-8 py-6">
                {t('home.hero.contactNow')}
              </Button>
            </Link>
            <Link to="/galeria">
              <Button size="lg" variant="outline" className="border-2 border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-black text-lg px-8 py-6">
                {t('home.cta.viewGallery')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
